import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  timeout?: number;
  dismissible?: boolean;
  showBrowserNotification?: boolean;
  autoCloseBrowserNotification?: boolean;
  notificationId?: string; // Link to user notification ID for bidirectional sync
}

@Injectable({
  providedIn: 'root',
})
export class ToastService implements OnDestroy {
  private toasts$ = new BehaviorSubject<Toast[]>([]);
  private toastIdCounter = 0;
  private browserNotificationPermission: NotificationPermission = 'default';
  private browserNotifications = new Map<string, Notification>(); // Track browser notifications by notification ID
  private onBrowserNotificationClosed?: (notificationId: string) => void; // Callback for when browser notification is closed
  private cleanupInterval?: number; // Interval for checking orphaned notifications

  public readonly toasts = this.toasts$.asObservable();

  constructor() {
    this.checkBrowserNotificationPermission();
    this.setupNotificationCleanup();
  }

  private checkBrowserNotificationPermission(): void {
    if ('Notification' in window) {
      this.browserNotificationPermission = Notification.permission;
    }
  }

  private setupNotificationCleanup(): void {
    // Only check when the page becomes visible (user returns to tab)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkForOrphanedNotifications();
      }
    });
  }

  private checkForOrphanedNotifications(): void {
    // CONSERVATIVE APPROACH: Only detect orphans through exception handling
    // Don't assume notifications are orphaned just because page became visible
    const orphanedIds: string[] = [];

    this.browserNotifications.forEach((notification, notificationId) => {
      let isOrphaned = false;

      try {
        // Try to access notification properties
        // If the notification was closed externally, this might throw an exception
        const testTag = notification.tag;
        const testTitle = notification.title;

        // Only mark as orphaned if properties are explicitly null/undefined
        if (testTag === null && testTitle === null) {
          isOrphaned = true;
        }
      } catch (error) {
        // If accessing properties throws an error, the notification is definitely closed
        isOrphaned = true;
      }

      if (isOrphaned) {
        orphanedIds.push(notificationId);
      }
    });

    // Process orphaned notifications
    orphanedIds.forEach(notificationId => {
      this.browserNotifications.delete(notificationId);

      // Trigger the callback to mark the app notification as read
      if (this.onBrowserNotificationClosed) {
        this.onBrowserNotificationClosed(notificationId);
      }
    });
  }

  // Clean up when service is destroyed
  public ngOnDestroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
  }

  public async requestBrowserNotificationPermission(): Promise<NotificationPermission> {
    if ('Notification' in window && Notification.permission === 'default') {
      this.browserNotificationPermission = await Notification.requestPermission();
    }
    return this.browserNotificationPermission;
  }

  private showBrowserNotification(toast: Toast): void {
    if ('Notification' in window && this.browserNotificationPermission === 'granted' && toast.showBrowserNotification) {
      const notification = new Notification(toast.title, {
        body: toast.message,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: toast.notificationId || toast.id, // Use notification ID if available, otherwise toast ID
        requireInteraction: false,
        silent: false,
      });

      // Store reference to browser notification if we have a notification ID
      if (toast.notificationId) {
        this.browserNotifications.set(toast.notificationId, notification);
      }

      // Listen for browser notification close event
      notification.onclose = event => {
        if (toast.notificationId) {
          this.browserNotifications.delete(toast.notificationId);
          // Notify that browser notification was closed
          if (this.onBrowserNotificationClosed) {
            this.onBrowserNotificationClosed(toast.notificationId);
          }
        }
      };

      // Listen for browser notification click event
      notification.onclick = event => {
        // Focus the window when notification is clicked
        window.focus();

        // Mark as read when clicked
        if (toast.notificationId && this.onBrowserNotificationClosed) {
          this.onBrowserNotificationClosed(toast.notificationId);
        }

        notification.close();
      };

      // Auto-close browser notification if enabled and timeout is specified
      if (toast.autoCloseBrowserNotification && toast.timeout && toast.timeout > 0) {
        setTimeout(() => {
          notification.close();
        }, toast.timeout);
      }
    }
  }

  show(toast: Omit<Toast, 'id'>): string {
    const id = `toast-${++this.toastIdCounter}`;
    const newToast: Toast = {
      id,
      timeout: 5000,
      dismissible: true,
      showBrowserNotification: false,
      autoCloseBrowserNotification: true,
      ...toast,
    };

    const currentToasts = this.toasts$.value;
    this.toasts$.next([...currentToasts, newToast]);

    // Show browser notification if requested
    this.showBrowserNotification(newToast);

    if (newToast.timeout && newToast.timeout > 0) {
      setTimeout(() => this.dismiss(id), newToast.timeout);
    }

    return id;
  }

  success(
    title: string,
    message?: string,
    timeout?: number,
    showBrowserNotification?: boolean,
    autoCloseBrowserNotification?: boolean
  ): string {
    return this.show({
      type: 'success',
      title,
      message,
      ...(timeout !== undefined && { timeout }),
      ...(showBrowserNotification !== undefined && { showBrowserNotification }),
      ...(autoCloseBrowserNotification !== undefined && { autoCloseBrowserNotification }),
    });
  }

  error(
    title: string,
    message?: string,
    timeout?: number,
    showBrowserNotification?: boolean,
    autoCloseBrowserNotification?: boolean
  ): string {
    const minimumTimeout = 10_000; //At least 10 seconds for error messages if timeout is specified
    const effectiveTimeout = timeout !== undefined ? Math.max(timeout, minimumTimeout) : undefined;
    return this.show({
      type: 'error',
      title,
      message,
      ...(effectiveTimeout !== undefined && { timeout: effectiveTimeout }),
      ...(showBrowserNotification !== undefined && { showBrowserNotification }),
      ...(autoCloseBrowserNotification !== undefined && { autoCloseBrowserNotification }),
    });
  }

  warning(
    title: string,
    message?: string,
    timeout?: number,
    showBrowserNotification?: boolean,
    autoCloseBrowserNotification?: boolean
  ): string {
    return this.show({
      type: 'warning',
      title,
      message,
      ...(timeout !== undefined && { timeout }),
      ...(showBrowserNotification !== undefined && { showBrowserNotification }),
      ...(autoCloseBrowserNotification !== undefined && { autoCloseBrowserNotification }),
    });
  }

  info(
    title: string,
    message?: string,
    timeout?: number,
    showBrowserNotification?: boolean,
    autoCloseBrowserNotification?: boolean
  ): string {
    return this.show({
      type: 'info',
      title,
      message,
      ...(timeout !== undefined && { timeout }),
      ...(showBrowserNotification !== undefined && { showBrowserNotification }),
      ...(autoCloseBrowserNotification !== undefined && { autoCloseBrowserNotification }),
    });
  }

  // Convenience method for notification-style toasts (1 second duration with browser notification that doesn't auto-close)
  notification(
    title: string,
    message?: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
    notificationId?: string
  ): string {
    return this.show({
      type,
      title,
      message,
      timeout: 5000,
      showBrowserNotification: true,
      autoCloseBrowserNotification: false, // Don't auto-close browser notifications for these
      dismissible: true,
      notificationId, // Link to user notification ID
    });
  }

  // Set callback for when browser notifications are closed
  public setBrowserNotificationClosedCallback(callback: (notificationId: string) => void): void {
    this.onBrowserNotificationClosed = callback;
  }

  // Close browser notification by notification ID
  public closeBrowserNotification(notificationId: string): void {
    const browserNotification = this.browserNotifications.get(notificationId);
    if (browserNotification) {
      browserNotification.close();
      this.browserNotifications.delete(notificationId);
    }
  }

  // Close browser notifications by download job ID (for superseded download notifications)
  public closeBrowserNotificationsByDownloadJobId(downloadJobId: string): void {
    const notificationsToClose: string[] = [];

    // Find all browser notifications that match this download job ID
    this.browserNotifications.forEach((notification, notificationId) => {
      // Check if the notification tag contains the download job ID
      if (notification.tag && notification.tag.includes(downloadJobId)) {
        notificationsToClose.push(notificationId);
      }
    });

    // Close all matching notifications
    notificationsToClose.forEach(notificationId => {
      this.closeBrowserNotification(notificationId);
    });
  }

  // Check if a browser notification exists for a notification ID
  public hasBrowserNotification(notificationId: string): boolean {
    return this.browserNotifications.has(notificationId);
  }

  dismiss(id: string): void {
    const currentToasts = this.toasts$.value;
    this.toasts$.next(currentToasts.filter(toast => toast.id !== id));
  }

  clear(): void {
    this.toasts$.next([]);
  }
}
